import { formatError } from "../api.ts";

export interface Page<T> {
  readonly items: readonly T[];
  readonly cursor?: string;
  /** Shown instead of the usual status while the page is incomplete upstream. */
  readonly notice?: string;
}

export function PagedList<T>(
  load: (cursor?: string) => Promise<Page<T>>,
  key: (item: T) => string,
  /** Returning nothing skips the item (e.g. locally hidden posts). */
  render: (item: T) => Element | undefined,
  signal: AbortSignal,
  empty: string,
): Element {
  const rows = <div class="list-rows"></div>;
  const status = <p class="status" aria-live="polite"></p>;
  const more = <button class="load-more" type="button">Load more</button> as HTMLButtonElement;
  const seen = new Set<string>();
  let shown = 0;
  let cursor: string | undefined;
  let loading = false;

  const next = async () => {
    if (loading || signal.aborted) return;
    loading = true;
    more.disabled = true;
    status.textContent = "Loading…";
    try {
      const page = await load(cursor);
      if (signal.aborted) return;
      for (const item of page.items) {
        const id = key(item);
        if (seen.has(id)) continue;
        seen.add(id);
        const row = render(item);
        if (row) {
          rows.append(row);
          shown++;
        }
      }
      cursor = page.cursor;
      status.textContent = page.notice ??
        (shown ? "" : cursor ? "Nothing visible on this page." : empty);
      more.hidden = !cursor;
      more.textContent = "Load more";
    } catch (error) {
      if (signal.aborted) return;
      status.textContent = formatError(error);
      more.textContent = "Retry";
      more.hidden = false;
    } finally {
      loading = false;
      more.disabled = false;
    }
  };
  more.addEventListener("click", next);
  void next();
  return <div class="paged-list">{rows}{status}{more}</div>;
}
